import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { ApiErrorCode } from '@/api/apiTypes'
import {
  AppButton,
  AnimatedTextInput,
  StickyHeaderNavigation,
} from '@/components'
import { BaseView, BaseText } from '@/components/base'
import { inputLimits } from '@/constants/inputLimits'
import { useVerifyPassword } from '@/domain'
import { ForgotPasswordBtn } from '@/features/auth/enter-password/components'
import { useUserAuthSession } from '@/hooks'
import { useTelegramLink } from '@/hooks/useTelegramLink'
import { useAuthScopedStore } from '@/store/scoped'

export const EnterPasswordScreen: React.FC = () => {
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  // Store password ref for telegram linking (password is NOT persisted, only in component)
  const passwordRef = useRef('')

  const { t } = useTranslation()
  const navigate = useNavigate()

  const userId = useAuthScopedStore((state) => state.signInForm.userId)
  const email = useAuthScopedStore((state) => state.email)
  const phone = useAuthScopedStore((state) => state.phone)
  const { startSession } = useUserAuthSession()
  const { getCanLink, linkWithAccount } = useTelegramLink()

  const { mutateAsync: verifyPassword, isPending } = useVerifyPassword({
    onSuccess: async (data) => {
      await startSession({
        token: data.access_token,
        refreshToken: data.refresh_token,
      })

      // After successful login, try to link Telegram account if applicable
      // Password is passed directly from component state - never persisted
      // Use getCanLink() to get current value, avoiding stale closure issues
      const currentCanLink = getCanLink()
      if (currentCanLink && passwordRef.current) {
        // Construct phone number in E.164 format if available
        const phoneNumber = phone ? `${phone.countryCode}${phone.number}` : undefined

        // Link in background - don't block navigation
        linkWithAccount({
          email: email || undefined,
          phoneNumber,
          password: passwordRef.current,
        }).catch(() => {
          // Error already logged and toast shown by hook
          // Don't block user from proceeding
        })
      }

      // Navigate to dashboard (equivalent of "(tabs)" in original)
      navigate('/dashboard', { replace: true })
    },
    onError: ({ code, errorMessage }) => {
      if (!errorMessage) return

      if (
        code === ApiErrorCode.PASSWORD_INVALID ||
        code === ApiErrorCode.USER_NOT_FOUND
      ) {
        setErrorMessage(errorMessage)
        return
      }

      toast.error(errorMessage)
    },
  })

  const handleSignin = async () => {
    if (!userId) {
      toast.error('User ID is missing')
      return
    }

    // Store password in ref for telegram linking (never persisted)
    passwordRef.current = password

    await verifyPassword({
      user_id: userId,
      password,
    })
  }

  return (
    <BaseView flex={1} px={12} gap={16}>
      {/* Header */}
      <BaseView gap={28}>
        <StickyHeaderNavigation />
        <BaseText className="text-3xl font-bold">
          {t('form:labels.enterPassword')}
        </BaseText>
      </BaseView>

      {/* Password Input */}
      <AnimatedTextInput
        className="ym-hide-content"
        errorMessage={errorMessage}
        label={t('form:labels.enterPassword')}
        value={password}
        maxLength={inputLimits.password.max}
        secureTextEntry
        onChangeText={(newPassword) => {
          if (errorMessage) setErrorMessage('')
          setPassword(newPassword)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSignin()
          }
        }}
      />

      {/* Footer */}
      <BaseView mt="auto" gap={28} pb={12}>
        <ForgotPasswordBtn />

        <AppButton
          label={t('form:actions.confirm')}
          onClick={handleSignin}
          isLoading={isPending}
          disabled={!password.trim()}
          className="w-full"
        />
      </BaseView>
    </BaseView>
  )
}
